import React, { useEffect, useRef, useState } from 'react';
import { Canvas, extend, useThree, useFrame } from '@react-three/fiber';
import { useTexture, Environment, RoundedBox, Text } from '@react-three/drei';
import { BallCollider, CuboidCollider, Physics, RigidBody, useRopeJoint, useSphericalJoint } from '@react-three/rapier';
import { MeshLineGeometry, MeshLineMaterial } from 'meshline';
import * as THREE from 'three';
import './PhysicsLanyard.css';

extend({ MeshLineGeometry, MeshLineMaterial });

// Ribbon label component that follows the band - Vertical AS 2026
function RibbonLabel({ j1, j2 }) {
  const labelRef = useRef();

  useFrame(() => {
    if (j1.current && j2.current && labelRef.current) {
      // Position between j1 and j2
      const pos1 = j1.current.translation();
      const pos2 = j2.current.translation();
      labelRef.current.position.set(
        (pos1.x + pos2.x) / 2,
        (pos1.y + pos2.y) / 2,
        (pos1.z + pos2.z) / 2 + 0.02
      );
    }
  });

  return (
    <group ref={labelRef}>
      {/* Vertical text - rotated 90 degrees */}
      <Text
        fontSize={0.12}
        color="#444444"
        anchorX="center"
        anchorY="middle"
        fontWeight="bold"
        rotation={[0, 0, -Math.PI / 2]}
        position={[-0.15, 0, 0]}
      >
        AS
      </Text>
      <Text
        fontSize={0.08}
        color="#444444"
        anchorX="center"
        anchorY="middle"
        rotation={[0, 0, -Math.PI / 2]}
        position={[0.1, 0, 0]}
      >
        2026
      </Text>
    </group>
  );
}

function Band({ maxSpeed = 50, minSpeed = 10 }) {
  const band = useRef();
  const fixed = useRef();
  const j1 = useRef();
  const j2 = useRef();
  const j3 = useRef();
  const card = useRef();

  const vec = new THREE.Vector3();
  const ang = new THREE.Vector3();
  const rot = new THREE.Vector3();
  const dir = new THREE.Vector3();

  const segmentProps = {
    type: 'dynamic',
    canSleep: true,
    colliders: false,
    angularDamping: 4,
    linearDamping: 4
  };

  const qrTexture = useTexture('/qr-code.png');
  qrTexture.flipY = true;
  qrTexture.colorSpace = THREE.SRGBColorSpace;

  const { width, height } = useThree((state) => state.size);
  const { viewport } = useThree();
  // Use pixel width for reliable mobile detection
  // Use pixel width for reliable mobile detection
  const isMobile = width < 768;

  const [curve] = useState(
    () => new THREE.CatmullRomCurve3([
      new THREE.Vector3(),
      new THREE.Vector3(),
      new THREE.Vector3(),
      new THREE.Vector3()
    ])
  );
  const [dragged, drag] = useState(false);
  const [hovered, hover] = useState(false);

  // Dynamic segment length based on viewport to prevent ribbon disconnection
  // Scale segment length with viewport width to maintain proper rope physics
  const baseSegmentLength = isMobile ? 0.5 : 0.8;
  const viewportScaledSegment = baseSegmentLength * (viewport.width / 10); // Normalize to ~10 unit viewport
  const segmentLength = Math.max(0.3, Math.min(viewportScaledSegment, 1.2));

  useRopeJoint(fixed, j1, [[0, 0, 0], [0, 0, 0], segmentLength]);
  useRopeJoint(j1, j2, [[0, 0, 0], [0, 0, 0], segmentLength]);
  useRopeJoint(j2, j3, [[0, 0, 0], [0, 0, 0], segmentLength]);
  // Connect j3 to the card at the ring position (top of clip)
  // Adjust connection point based on scale
  const cardConnectionY = isMobile ? 0.65 : 0.4;
  useSphericalJoint(j3, card, [[0, 0, 0], [0, cardConnectionY, 0]]);

  useEffect(() => {
    if (hovered) {
      document.body.style.cursor = dragged ? 'grabbing' : 'grab';
      return () => void (document.body.style.cursor = 'auto');
    }
  }, [hovered, dragged]);

  useFrame((state, delta) => {
    if (dragged) {
      vec.set(state.pointer.x, state.pointer.y, 0.5).unproject(state.camera);
      dir.copy(vec).sub(state.camera.position).normalize();
      vec.add(dir.multiplyScalar(state.camera.position.length()));
      [card, j1, j2, j3, fixed].forEach((ref) => ref.current?.wakeUp());
      card.current?.setNextKinematicTranslation({
        x: vec.x - dragged.x,
        y: vec.y - dragged.y,
        z: vec.z - dragged.z
      });
    }

    if (fixed.current) {
      [j1, j2].forEach((ref) => {
        if (!ref.current.lerped) {
          ref.current.lerped = new THREE.Vector3().copy(ref.current.translation());
        }
        const clampedDistance = Math.max(
          0.1,
          Math.min(1, ref.current.lerped.distanceTo(ref.current.translation()))
        );
        ref.current.lerped.lerp(
          ref.current.translation(),
          delta * (minSpeed + clampedDistance * (maxSpeed - minSpeed))
        );
      });

      // Lerp j3 as well for smoother ribbon
      if (!j3.current.lerped) {
        j3.current.lerped = new THREE.Vector3().copy(j3.current.translation());
      }
      const j3Distance = Math.max(
        0.1,
        Math.min(1, j3.current.lerped.distanceTo(j3.current.translation()))
      );
      j3.current.lerped.lerp(
        j3.current.translation(),
        delta * (minSpeed + j3Distance * (maxSpeed - minSpeed))
      );

      // Connect ribbon directly through the physics joints
      curve.points[0].copy(j3.current.lerped);
      curve.points[1].copy(j2.current.lerped);
      curve.points[2].copy(j1.current.lerped);
      curve.points[3].copy(fixed.current.translation());
      band.current.geometry.setPoints(curve.getPoints(32));

      ang.copy(card.current.angvel());
      rot.copy(card.current.rotation());
      card.current.setAngvel({ x: ang.x, y: ang.y - rot.y * 0.25, z: ang.z });
    }
  });

  curve.curveType = 'chordal';
  qrTexture.anisotropy = 16;

  // Responsive Position Calculation
  // Mobile: Strictly centered [0, 4, 0]
  // Desktop: Dynamic X to ensure it stays in view on narrower displays
  // Target 4.2 but clamp to (viewport.width/2) - 1.0 to keep it inside the edge
  const maxDesktopX = 4.2;
  const clampedX = Math.min(maxDesktopX, (viewport.width / 2) - 1.0);

  const responsivePos = isMobile ? [0, 2.8, 0] : [clampedX, 5, 0];

  // Fix scale to be viewport-aware - prevent enormous lanyard on small screens
  // Base scale on viewport width, with reasonable min/max bounds
  const baseScale = isMobile ? 0.7 : 0.35;
  const viewportScale = baseScale * Math.min(1.5, viewport.width / 8);
  const responsiveScale = Math.max(0.3, Math.min(viewportScale, isMobile ? 1.0 : 0.6));

  return (
    <>
      <group position={responsivePos}>
        <RigidBody ref={fixed} {...segmentProps} type="fixed" />
        <RigidBody position={[0.6, 0, 0]} ref={j1} {...segmentProps}>
          <BallCollider args={[0.1]} />
        </RigidBody>
        <RigidBody position={[1.2, 0, 0]} ref={j2} {...segmentProps}>
          <BallCollider args={[0.1]} />
        </RigidBody>
        <RigidBody position={[1.8, 0, 0]} ref={j3} {...segmentProps}>
          <BallCollider args={[0.1]} />
        </RigidBody>
        <RigidBody
          position={[2.4, 0, 0]}
          ref={card}
          {...segmentProps}
          type={dragged ? 'kinematicPosition' : 'dynamic'}
        >
          <CuboidCollider args={[0.19, 0.26, 0.01]} />
          <group
            scale={responsiveScale}
            position={[0, 0, 0]}
            onPointerOver={() => hover(true)}
            onPointerOut={() => hover(false)}
            onPointerUp={(e) => {
              e.target.releasePointerCapture(e.pointerId);
              drag(false);
            }}
            onPointerDown={(e) => {
              e.target.setPointerCapture(e.pointerId);
              drag(new THREE.Vector3().copy(e.point).sub(vec.copy(card.current.translation())));
            }}
          >
            {/* Main card - User's CSS dimensions: 355px x 530px, aspect ratio 0.67 */}
            {/* Converting to Three.js units: maintaining aspect ratio */}
            <RoundedBox args={[1.6, 2.39, 0.035]} radius={0} smoothness={4}>
              <meshPhysicalMaterial
                color="#090a0a"
                metalness={0.1}
                roughness={0.2}
                clearcoat={1}
                clearcoatRoughness={0.1}
                transmission={0.6}
                thickness={1.5}
                envMapIntensity={2}
              />
            </RoundedBox>

            {/* Card edge/border highlight - Metallic rim */}
            <RoundedBox args={[1.62, 2.41, 0.032]} radius={0} smoothness={4}>
              <meshPhysicalMaterial
                color="#ffffff"
                metalness={1}
                roughness={0.1}
                transparent={true}
                opacity={0.3}
                envMapIntensity={3}
              />
            </RoundedBox>

            {/* QR Code printed directly on card - FRONT */}
            <mesh position={[0, 0, 0.018]}>
              <planeGeometry args={[1.45, 1.45]} />
              <meshStandardMaterial
                map={qrTexture}
                transparent={true}
                opacity={0.9}
                toneMapped={false}
                roughness={0.5}
                metalness={0.5}
                polygonOffset={true}
                polygonOffsetFactor={-1}
              />
            </mesh>

            {/* QR Code printed directly on card - BACK */}
            <mesh position={[0, 0, -0.018]} rotation={[0, Math.PI, 0]}>
              <planeGeometry args={[1.45, 1.45]} />
              <meshStandardMaterial
                map={qrTexture}
                transparent={true}
                opacity={0.9}
                toneMapped={false}
                roughness={0.5}
                metalness={0.5}
                polygonOffset={true}
                polygonOffsetFactor={-1}
              />
            </mesh>

            {/* === CLIP ASSEMBLY - Vercel style === */}

            {/* Main plastic clip body - dark with rounded edges */}
            <RoundedBox args={[0.4, 0.35, 0.08]} radius={0.04} smoothness={4} position={[0, 1.22, 0]}>
              <meshPhysicalMaterial
                color="#1a1a1a"
                metalness={0.8}
                roughness={0.2}
                clearcoat={1}
                clearcoatRoughness={0.1}
              />
            </RoundedBox>

            {/* Clip top ridge */}
            <RoundedBox args={[0.42, 0.06, 0.09]} radius={0.02} smoothness={4} position={[0, 1.36, 0]}>
              <meshPhysicalMaterial
                color="#222222"
                metalness={0.15}
                roughness={0.5}
              />
            </RoundedBox>

            {/* Metal insert/plate on clip */}
            <mesh position={[0, 1.22, 0.041]}>
              <planeGeometry args={[0.28, 0.22]} />
              <meshStandardMaterial
                color="#2d2d2d"
                metalness={0.7}
                roughness={0.3}
              />
            </mesh>

            {/* Clip slot/opening */}
            <mesh position={[0, 1.15, 0.042]}>
              <planeGeometry args={[0.2, 0.06]} />
              <meshStandardMaterial color="#0a0a0a" />
            </mesh>

            {/* Metal ring at top for lanyard attachment */}
            <mesh position={[0, 1.42, 0]} rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[0.06, 0.015, 16, 32]} />
              <meshStandardMaterial
                color="#4a4a4a"
                metalness={0.9}
                roughness={0.2}
                envMapIntensity={1.5}
              />
            </mesh>

            {/* Small rivet/button detail on clip */}
            <mesh position={[0, 1.28, 0.045]}>
              <circleGeometry args={[0.025, 16]} />
              <meshStandardMaterial
                color="#3a3a3a"
                metalness={0.8}
                roughness={0.2}
              />
            </mesh>
          </group>
        </RigidBody>
      </group>

      {/* 
        RIBBON/LANYARD STRAP - Lanyard cord
      */}
      <mesh ref={band}>
        <meshLineGeometry />
        <meshLineMaterial
          color="#1a1a1a"
          depthTest={false}
          resolution={[width, height]}
          lineWidth={0.5}
        />
      </mesh>

      {/* AS Label on ribbon - follows j1/j2 position */}
      <RibbonLabel j1={j1} j2={j2} />
    </>
  );
}

export default function PhysicsLanyard() {
  return (
    <div className="physics-lanyard-container">
      <Canvas
        camera={{ position: [0, 0, 13], fov: 25 }}
        gl={{ alpha: true, antialias: false, powerPreference: 'high-performance' }}
        dpr={[1, 2]} // Support high-DPI screens for sharper text/QR
        performance={{ min: 0.5 }}
        eventSource={document.getElementById('root')}
        style={{ pointerEvents: 'none' }}
      >
        <ambientLight intensity={Math.PI * 0.5} />
        <spotLight position={[10, 10, 10]} angle={0.3} penumbra={1} intensity={1.5} castShadow={false} />
        <Physics gravity={[0, -40, 0]} timeStep={1 / 90}>
          <Band />
        </Physics>
        <Environment preset="city" blur={1} />
      </Canvas>
    </div>
  );
}
